import React, { useState, useEffect } from 'react'
import { ObsidianGlassCard } from '../obsidian/ObsidianGlassCard'
import { BadgeChip } from '../editorial/BadgeChip'
import { readBlobFromOPFS } from '../../lib/storage/opfs'
import { downloadSingleResult } from '../../lib/export-zip'
import type { FileRecord } from '../../lib/storage/types'

export interface FileQueueBentoProps {
  records: FileRecord[]
  onDeleteRecord: (id: string) => void
  onInspectRecord: (record: FileRecord) => void
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const FileItemCard: React.FC<{
  record: FileRecord
  onDelete: () => void
  onInspect: () => void
}> = ({ record, onDelete, onInspect }) => {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null)
  const [downloadingFormat, setDownloadingFormat] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    let url: string | null = null

    async function loadThumb() {
      if (record.category === 'image' || record.category === 'svg') {
        const blob = await readBlobFromOPFS(record.opfsPath)
        if (blob && active) {
          url = URL.createObjectURL(blob)
          setThumbUrl(url)
        }
      }
    }

    loadThumb()

    return () => {
      active = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [record.opfsPath, record.category])

  const handleDownload = async (opfsPath: string, filename: string, format: string) => {
    try {
      setDownloadingFormat(format)
      await downloadSingleResult(opfsPath, filename)
    } catch (err) {
      console.error('Error downloading single file:', err)
    } finally {
      setDownloadingFormat(null)
    }
  }

  const isDone = record.status === 'done'
  const isProcessing = record.status === 'processing'
  const isError = record.status === 'error'

  return (
    <ObsidianGlassCard
      variant="bento"
      spotlight
      style={{ transition: 'all 200ms ease' }}
      innerStyle={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {/* Top Row: Thumbnail + Title & Status + Delete */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        {/* Thumbnail Preview */}
        <div
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={record.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <i
              className={`pi ${
                record.category === 'video'
                  ? 'pi-video'
                  : record.category === 'svg'
                  ? 'pi-file-edit'
                  : 'pi-image'
              }`}
              style={{
                fontSize: '22px',
                color:
                  record.category === 'video'
                    ? 'var(--c-accent-electric, #38BFFF)'
                    : 'rgba(255, 255, 255, 0.4)',
              }}
            />
          )}
        </div>

        {/* File Details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              marginBottom: '4px',
            }}
          >
            <h4
              style={{
                fontSize: '14.5px',
                fontWeight: 600,
                color: '#ffffff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={record.name}
            >
              {record.name}
            </h4>

            <button
              type="button"
              onClick={onDelete}
              aria-label="Eliminar archivo"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.35)',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#ff6b6b')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.35)')}
            >
              <i className="pi pi-trash" style={{ fontSize: '13px' }} />
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <span className="tabular-nums">Original: {formatBytes(record.originalSize)}</span>
            <span>•</span>
            <span style={{ textTransform: 'uppercase' }}>{record.category}</span>
          </div>
        </div>
      </div>

      {/* Middle Row: Progress or Error State */}
      {isProcessing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: 'var(--c-pulse, #2AE6B6)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '12px' }} />
              Optimizando en Web Workers...
            </span>
          </div>
          <div
            style={{
              width: '100%',
              height: '4px',
              borderRadius: '9999px',
              background: 'rgba(255, 255, 255, 0.08)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: '60%',
                height: '100%',
                background: 'var(--c-pulse, #2AE6B6)',
                borderRadius: '9999px',
                animation: 'wst-spin 2s linear infinite',
              }}
            />
          </div>
        </div>
      )}

      {isError && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: '10px',
            background: 'rgba(229, 72, 77, 0.1)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            fontSize: '12px',
            color: '#ff8588',
          }}
        >
          <i className="pi pi-exclamation-circle" style={{ marginRight: '6px' }} />
          {record.error || 'Fallo de procesamiento local'}
        </div>
      )}

      {/* Bottom Row: Generated Results & Actions */}
      {isDone && record.results && record.results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.45)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <span>Salidas Generadas</span>
            {(record.category === 'image' || record.category === 'video') && (
              <button
                type="button"
                onClick={onInspect}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--c-accent-electric, #38BFFF)',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
              >
                <i className="pi pi-sliders-v" style={{ fontSize: '10px' }} />
                Inspeccionar Fidelidad
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {record.results.map((res) => {
              const isDownloading = downloadingFormat === res.format
              const isPositive = res.savingsPercent > 0

              return (
                <div
                  key={res.filename}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.07)',
                    fontSize: '12px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-data, 'Space Grotesk', monospace)",
                      fontWeight: 700,
                      color: '#ffffff',
                    }}
                  >
                    {res.label}
                  </span>

                  <span
                    className="tabular-nums"
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    {formatBytes(res.size)}
                  </span>

                  {isPositive && (
                    <span
                      className="tabular-nums"
                      style={{
                        color: 'var(--c-pulse, #2AE6B6)',
                        fontWeight: 600,
                        fontSize: '11px',
                        background: 'rgba(42, 230, 182, 0.12)',
                        padding: '1px 5px',
                        borderRadius: '4px',
                      }}
                    >
                      -{res.savingsPercent}%
                    </span>
                  )}

                  <button
                    type="button"
                    title={`Descargar ${res.filename}`}
                    onClick={() => handleDownload(res.opfsPath, res.filename, res.format)}
                    disabled={isDownloading}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--c-accent-electric, #38BFFF)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px',
                    }}
                  >
                    <i
                      className={`pi ${
                        isDownloading ? 'pi-spin pi-spinner' : 'pi-download'
                      }`}
                      style={{ fontSize: '11px' }}
                    />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </ObsidianGlassCard>
  )
}

export const FileQueueBento: React.FC<FileQueueBentoProps> = ({
  records,
  onDeleteRecord,
  onInspectRecord,
}) => {
  if (records.length === 0) return null

  return (
    <section style={{ marginBottom: '48px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i
            className="pi pi-list"
            style={{ color: 'var(--c-accent-electric, #38BFFF)', fontSize: '16px' }}
          />
          <h3
            style={{
              fontFamily: "var(--font-display, 'Unbounded', sans-serif)",
              fontSize: '16px',
              fontWeight: 700,
              color: '#ffffff',
            }}
          >
            Cola de Procesamiento ({records.length})
          </h3>
        </div>

        <BadgeChip variant="electric">
          CÓMPUTO MULTI-HILO ACTIVO
        </BadgeChip>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px',
        }}
      >
        {records.map((record) => (
          <FileItemCard
            key={record.id}
            record={record}
            onDelete={() => onDeleteRecord(record.id)}
            onInspect={() => onInspectRecord(record)}
          />
        ))}
      </div>
    </section>
  )
}
